import {useCallback} from "react"
import {useDispatch, useSelector} from "react-redux"
import {authSlice} from "scholastic-client-components/src/slices/authSlice"
import {AuthResponse, LoginRequest} from "scholastic-client-components/src/api/authAPI"
import {v4 as uuidv4} from "uuid"
import {authAPI} from "../../api/connectedAPI"
import {redirectToReferrer} from "../../lib/routing"
import {useCanvaHeaders} from "../useCanvaHeaders"
import {useAuthStorage} from "scholastic-client-components/src/hooks/useAuthStorage"
import {Portal} from "scholastic-client-components/src/types/securityTypes"
import {SafeLocalStorage} from "scholastic-client-components/src/utilities/SafeLocalStorage"

const getBrowserId = () => {
    const secretName = "googleAdRay" // non-related name for more security
    let value = SafeLocalStorage.getItem(secretName)
    if (!value) {
        value = uuidv4()
        if (value) SafeLocalStorage.setItem(secretName, value)
    }
    return value || "defaultId"
}

export const useLoginAPI = () => {
    const dispatch = useDispatch()
    const canvaHeaders = useCanvaHeaders()
    const {setValue} = useAuthStorage(useSelector)

    const auth = useCallback(() => {
        dispatch(
            (authAPI.auth as any)({
                config: {
                    headers: canvaHeaders,
                },
                includeHeaders: ["etag"],
                onSuccess: (response: AuthResponse, _: any, {etag}: any) => {
                    if (canvaHeaders["canva-id"]) {
                        window.location.replace(
                            "/api/canva/redirect?state=" + canvaHeaders["canva-state"]
                        )
                        return
                    }
                    dispatch(
                        authSlice.actions.successAuth({
                            ...response,
                            etag,
                            portal: Portal.Auth,
                        })
                    )
                    setValue()
                    setTimeout(redirectToReferrer, 0)
                },
                onError: (errorResponse) => {
                    dispatch(authSlice.actions.failLogin(errorResponse))
                },
            })
        )
    }, [canvaHeaders, dispatch])

    return useCallback(
        (payload: LoginRequest) => {
            dispatch(authSlice.actions.startLogin())
            const browserId = getBrowserId()
            dispatch(
                authAPI.login({
                    onError: (response) => dispatch(authSlice.actions.failLogin(response)),
                    onSuccess: (response) => {
                        if (response.SSOLink) {
                            window.location.replace(response.SSOLink)
                            return
                        }

                        const suspendAuth =
                            response.nonConfirmedAccount ||
                            response.request2fa ||
                            response.requestTOSAgreement

                        if (suspendAuth) {
                            dispatch(
                                authSlice.actions.successLogin({...response, request: payload})
                            )
                        } else {
                            auth()
                        }
                    },
                    payload: {
                        ...payload,
                        browserId,
                    },
                })
            )
        },
        [auth, dispatch]
    )
}
